import * as styles from "./TreeItem.css";

import { clsx } from "clsx";
import type { JSX, KeyboardEvent } from "react";

import { Collapsible as BaseCollapsible } from "@base-ui/react/collapsible";
import { Icon } from "@frontend/components/atoms/Icon";

import type { TreeItemProps } from "./Tree.types";
import { TreeGuides } from "./TreeGuides";

function isFolderNode({ type, children }: TreeItemProps["node"]): boolean {
  return type === "folder" || (type === undefined && children !== undefined);
}

export function TreeItem({
  node,
  onSelect,
  nested = false,
  last = false,
}: TreeItemProps): JSX.Element {
  const isFolder = isFolderNode(node);
  const children = node.children ?? [];
  const isExpandable = isFolder && children.length > 0;
  const iconName = node.icon ?? (isFolder ? "Folder" : "File");

  const content = (
    <>
      {isExpandable && (
        <span className={styles.chevronSlot}>
          <Icon name="ChevronRight" size="sm" className={styles.chevron} />
        </span>
      )}
      <Icon name={iconName} size="sm" className={styles.icon} />
      <span className={styles.label}>{node.label}</span>
    </>
  );

  const guides = nested ? <TreeGuides last={last} /> : null;

  if (isExpandable) {
    return (
      <div className={nested ? styles.nestedIndent : undefined}>
        {guides}
        <BaseCollapsible.Root
          defaultOpen={node.defaultExpanded ?? true}
          disabled={node.disabled}
          className={styles.collapsibleRoot}
        >
          <BaseCollapsible.Trigger
            data-slot="tree-row"
            className={styles.row}
            render={<button type="button" />}
          >
            {content}
          </BaseCollapsible.Trigger>
          <BaseCollapsible.Panel
            data-slot="tree-panel"
            className={styles.panel}
          >
            <div className={styles.group}>
              {children.map((child, index) => (
                <TreeItem
                  key={child.id ?? `${child.label}-${index}`}
                  node={child}
                  onSelect={onSelect}
                  nested
                  last={index === children.length - 1}
                />
              ))}
            </div>
          </BaseCollapsible.Panel>
        </BaseCollapsible.Root>
      </div>
    );
  }

  const interactive = Boolean(onSelect) && !node.disabled;

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect?.(node);
    }
  }

  return (
    <div className={nested ? styles.nestedIndent : undefined}>
      {guides}
      <div
        data-slot="tree-row"
        className={clsx(styles.row, interactive && styles.rowInteractive)}
        role={interactive ? "button" : undefined}
        tabIndex={interactive ? 0 : undefined}
        aria-disabled={node.disabled || undefined}
        onClick={interactive ? () => onSelect?.(node) : undefined}
        onKeyDown={interactive ? handleKeyDown : undefined}
      >
        {content}
      </div>
    </div>
  );
}
